import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { missionsApi } from '../lib/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { 
  Plus, 
  Map, 
  Trash2, 
  Eye,
  CheckCircle,
  AlertTriangle,
  Calendar,
  Clock
} from 'lucide-react';

export default function MissionsPage() {
  const { t } = useTranslation();
  const [missions, setMissions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadMissions();
  }, []);

  const loadMissions = async () => {
    try {
      const response = await missionsApi.getAll();
      setMissions(response.data);
    } catch (error) {
      toast.error(t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (missionId) => {
    if (!window.confirm(t('drones.deleteConfirm'))) return;

    try {
      await missionsApi.delete(missionId);
      toast.success(t('common.success'));
      loadMissions();
    } catch (error) {
      toast.error(t('common.error'));
    }
  };

  const getRiskConfig = (level) => {
    const configs = {
      LOW: { 
        class: 'badge-safe', 
        label: t('dashboard.risk.low'), 
        icon: CheckCircle,
        bgClass: 'bg-emerald-50 border-emerald-200'
      },
      MODERATE: { 
        class: 'badge-warning', 
        label: t('dashboard.risk.moderate'), 
        icon: AlertTriangle,
        bgClass: 'bg-amber-50 border-amber-200'
      },
      HIGH: { 
        class: 'badge-danger', 
        label: t('dashboard.risk.high'), 
        icon: AlertTriangle,
        bgClass: 'bg-red-50 border-red-200'
      },
    };
    return configs[level] || configs.LOW;
  };

  return (
    <DashboardLayout
      title={t('missions.title')}
      actions={
        <Link to="/missions/new">
          <Button className="bg-sky-500 hover:bg-sky-600 text-white" data-testid="new-mission-btn">
            <Plus className="w-4 h-4 mr-2" />
            {t('missions.create')}
          </Button>
        </Link>
      }
    >
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sky-500"></div>
        </div>
      ) : missions.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-lg p-12 text-center">
          <Map className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-slate-900 mb-2 font-[Barlow]">
            {t('missions.empty')}
          </h3>
          <p className="text-slate-600 mb-6">
            {t('missions.emptyMessage')}
          </p>
          <Link to="/missions/new">
            <Button className="bg-sky-500 hover:bg-sky-600 text-white" data-testid="create-first-mission-btn">
              <Plus className="w-4 h-4 mr-2" />
              {t('missions.createFirst')}
            </Button>
          </Link>
        </div>
      ) : (
        <div className="space-y-4">
          {missions.map((mission) => {
            const riskConfig = getRiskConfig(mission.regulation_result?.risk_level);
            const RiskIcon = riskConfig.icon;

            return (
              <div
                key={mission.id}
                className={`bg-white border rounded-lg p-6 hover:shadow-lg transition-shadow duration-200 ${riskConfig.bgClass}`}
                data-testid={`mission-card-${mission.id}`}
              >
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-semibold text-slate-900 font-[Barlow]">
                        {mission.mission_type}
                      </h3>
                      <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium ${riskConfig.class}`}>
                        <RiskIcon className="w-3 h-3" />
                        {riskConfig.label}
                      </span>
                    </div>

                    <div className="flex flex-wrap items-center gap-4 text-sm text-slate-600">
                      <div className="flex items-center gap-1.5">
                        <Calendar className="w-4 h-4 text-slate-400" />
                        {new Date(mission.mission_date).toLocaleDateString()}
                      </div>
                      <div className="flex items-center gap-1.5">
                        <Clock className="w-4 h-4 text-slate-400" />
                        {mission.mission_time}
                      </div>
                      <div className="font-mono text-slate-500">
                        Alt: {mission.altitude_requested}m
                      </div>
                    </div>

                    {mission.regulation_result?.warnings?.length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-2">
                        {mission.regulation_result.warnings.slice(0, 2).map((warning, idx) => (
                          <span key={idx} className="inline-flex items-center px-2 py-1 bg-red-100 text-red-700 text-xs rounded">
                            <AlertTriangle className="w-3 h-3 mr-1" />
                            {warning.split(':')[0]}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="flex items-center gap-4">
                    <div className="text-center">
                      <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">{t('common.score')}</p>
                      <p className={`text-2xl font-bold font-mono ${
                        mission.risk_score <= 40 ? 'text-emerald-600' :
                        mission.risk_score <= 70 ? 'text-amber-600' : 'text-red-600'
                      }`}>
                        {mission.risk_score}
                      </p>
                    </div>

                    <div className="flex items-center gap-2">
                      <Link to={`/missions/${mission.id}`}>
                        <Button variant="outline" size="sm" data-testid={`view-mission-${mission.id}`}>
                          <Eye className="w-4 h-4 mr-1" />
                          {t('common.viewAll').split(' ')[0]}
                        </Button>
                      </Link>
                      <button
                        onClick={() => handleDelete(mission.id)}
                        className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                        data-testid={`delete-mission-${mission.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </DashboardLayout>
  );
}
